import React, {
	useEffect,
	useState,
	useCallback,
	useContext,
	useRef,
	Fragment as Frag
} from 'react';
import PropTypes from 'prop-types';

import {
	makeStyles,
	useTheme,
	useMediaQuery,
	Grid,
	Typography,
} from '@material-ui/core';

import Fido from "../utils/Fido";
import { isJsonEqual } from "../utils/Json";
import Constants from "../utils/Constants";
import { usePrevious } from "../utils/CustomHooks";
import { useFilterHandler, useStickyFilterOptions, useStickyFilterHandler } from "../utils/FilterHooks";
import AuthorizedUser from "../utils/AuthorizedUser";
import AuthorizedUserContext from "../utils/AuthorizedUserContext";
import InboxTabs from "../components/InboxTabs";
import InboxFilters from "../components/InboxFilters";
import AccountFilterField from "../components/AccountFilterField";
import PaddedContent from "../components/PaddedContent";
import ActivityList from "../components/ActivityList";
import Activity from "../containers/Activity";
import UpgradeFreemiumDialog from "../components/UpgradeFreemiumDialog";

/*import ActivityList from "../components/ActivityList";
//import AutoReload from "../components/AutoReload";
import Activity from "./Activity";*/

// TEMP
// ***********************
//import {activities, activities2} from "../data/activities.js";
// ***********************

const styles = makeStyles(theme => ({
	paddedContent: {
		[theme.breakpoints.down('xs')]: {
			padding: theme.spacing(0.5)
		}
	},

	mainGridItem: {
		[theme.breakpoints.up('sm')]: {
			paddingTop: theme.spacing(3)
		}
	},

	savedTab: {
		width: theme.spacing(11),

		"&:before": {
			width: 1,
			left: 0,
			content: '""',
			position: "absolute",
			display: "block",
			height: theme.spacing(2.5),
			backgroundColor: theme.palette.primary.darkBlue
		}
	},

	tab: {
		fontWeight: 400
	},

	bold: {
		fontWeight: 750
	},

	scrollGrid: {
		backgroundColor: "#f4f5f5"
	},

	searchResult: {
		fontSize: 16,
		marginTop: theme.spacing(2),
		display: 'block'
	}
}));

let actionPending = [];
let changingStatus = false;

// short constants
const statusConstants = Constants.ACTIVITY.STATUSES;
const savedConstants = Constants.ACTIVITY.CATEGORIES.SAVED;

// defaults
const defaultTab = Constants.ACTIVITY.STATUSES.NEW.ID;

// so we can cancel the call if need be
const topicFido = new Fido();
const activitiesFido = new Fido();
const pagingFido = new Fido();
const selectedActivityFido = new Fido();

const Activities = React.memo(function Activities(props) {
	let { updateHeader, location, showSnackbar, history } = props;

	// so we can avoid trying to update state on unmounted component (ie loadMore())
	const isMountedRef = useRef(true);

	const filterHandlerHook = useFilterHandler();
	const stickyFilterHandler = useStickyFilterHandler();

	const theme = useTheme();
	const classes = styles();
	const isDetails = location.pathname.match(/\/inbox\/[0-9]+/ig);
	const atLeastSM = useMediaQuery(theme.breakpoints.up("sm"), { noSsr: true });

	const authenticatedUser = useContext(AuthorizedUserContext);
	const [searchTerm, setSearchTerm] = useState('');
	const [selected, setSelected] = useState({
		tab: defaultTab,
		activity: null,
		activityId: null,
		index: isDetails && atLeastSM ? 0 : -1
	});
	const [activityFidoError, setActivityFidoError] = useState(false);
	const [upgradeFreemiumOpen, setUpgradeFreemiumOpen] = useState(false);

	const getDefaultCategories = (selectedTab) => {
		let tab = selectedTab || selected.tab,
			resolvedOrSaved = tab === Constants.ACTIVITY.STATUSES.RESOLVED.ID
				|| tab === Constants.ACTIVITY.CATEGORIES.SAVED.FILTER.ID;

		const categoriesArray = JSON.parse(localStorage.getItem('category')) || [];
		const alert = categoriesArray && categoriesArray[0];
		const discussions = categoriesArray && categoriesArray[1];

		return [{
			selected: alert ? alert.selected : true,
			id: Constants.ACTIVITY.CATEGORIES.ALERTS.FILTER.ID,
			name: Constants.ACTIVITY.CATEGORIES.ALERTS.FILTER.NAME
		}, {
			selected: discussions ? discussions.selected : resolvedOrSaved,
			id: Constants.ACTIVITY.CATEGORIES.DISCUSSIONS.FILTER.ID,
			name: Constants.ACTIVITY.CATEGORIES.DISCUSSIONS.FILTER.NAME
		}];
	};

	const getDefaultSources = () => {
		return Constants.SOURCES.getAuthorized(authenticatedUser);
	};

	const defaultFilterAccounts = useStickyFilterOptions(
		AuthorizedUser.hasStickyAccountFilter(authenticatedUser),
		authenticatedUser,
		AccountFilterField.stickyLocalStorageKey,
		authenticatedUser.childAccounts
	);

	const [categories, setCategories] = useState(getDefaultCategories());
	const [sources, setSources] = useState(getDefaultSources());
	const [accounts, setAccounts] = useState(defaultFilterAccounts);
	const [filter, setFilter] = useState({
		view: null,
		source: null,
		topic: null,
		account: defaultFilterAccounts,
		category: getDefaultCategories(),
		status: Constants.ACTIVITY.STATUSES.NEW.ID
	});
	const [topics, setTopics] = useState([]);

	const topicCache = useRef(topics);
	const getDefaultTopics = () => {
		return topicCache.current.map(topic => {
			topic.selected = true;
			return topic;
		});
	};

	const [data, setData] = useState({
		lastPage: 1,
		loading: false,
		activities: null,
		hasMoreItems: false
	});
	const [originalData, setOriginalData] = useState({
		lastPage: 1,
		loading: false,
		activities: null,
		hasMoreItems: false
	});
	const [searchResultLength, setSearchResultLength] = useState(0);


	const [animateOutId, setAnimateOutId] = useState(null);
	const [scrollTo, setScrollTo] = useState(0);

	// called once at the beginning
	useEffect(() => {
		loadTopics();

		// fix the scrolling / overflow
		document.body.style.overflow = "hidden";

		return () => {
			isMountedRef.current = false;
			setTimeout(() => document.body.style.overflow = "auto", 0);
		};

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// update header / title
	// RETURNS TRUE NOW, UPDATE LOGIC IF WANT - for some reason atLeastSM is false at first... so update title conditionally
	const [title, setTitle] = useState("signals");
	let prevTitle = usePrevious(title);
	let prevTab = usePrevious(selected.tab);
	useEffect(() => {
		if (prevTitle !== title || prevTab !== selected.tab || !atLeastSM) {
			let newTitle;
			if (atLeastSM) {
				Object.keys(statusConstants).map(key => {
					if (statusConstants[key].ID === selected.tab) {
						newTitle = statusConstants[key].NAME;
					} else if (savedConstants.FILTER.ID === selected.tab) {
						newTitle = savedConstants.FILTER.NAME;
					}
					return newTitle;
				});
			} else if (!atLeastSM && !isDetails) {
				newTitle = "signals";
			} else {
				newTitle = isDetails ? "details" : title;
			}

			console.log("updateHeader useEffect - prevTitle, title: ", prevTitle, newTitle);
			setTitle(newTitle);
			updateHeader({
				title: newTitle,
				primary: atLeastSM || !isDetails
			});
		}
	}, [selected.tab, prevTab, atLeastSM, updateHeader, isDetails, prevTitle, title, setTitle]);

	const loadActivities = useCallback(async () => {
		let query = { ...filter };
		Object.keys(filter).forEach(key => {
			if (filter[key] != null && Array.isArray(filter[key])) {
				let array = [];
				array = filter[key].filter(item => {
					return item.selected;
				});
				query[key] = array.map(item => {
					return item.id;
				});
			}
		});

		console.log("loadActivities - filter, query: ", filter, query, data);

		setData(prevData => {
			return { ...prevData, ...{ loading: true, activities: null } };
		});
		setOriginalData(prevData => {
			return { ...prevData, ...{ loading: true, activities: null } };
		});
		activitiesFido.dropIt();
		let resp = await activitiesFido.fetch("/activities", { query: query })
			.catch(error => showSnackbar(error.message));

		if (resp && isMountedRef.current) {
			// TEMP
			//resp.activities = (Math.random()*10) > 5 ? activities.activities : activities2.activities;
			// ----
			setData(prevData => {
				console.log("loadActivities - resp, setData: ", resp, { ...prevData, ...resp, ...{ loading: false, lastPage: resp.lastPage || 1 } });
				return { ...prevData, ...resp, ...{ loading: false, lastPage: resp.lastPage || 1 } };
			});
			setOriginalData(prevData => {
				return { ...prevData, ...resp, ...{ loading: false, lastPage: resp.lastPage || 1 } };
			});
			let filteredActivities = [];
			if (searchTerm.length > 2) {
				setSearchTerm(searchTerm);
				filteredActivities = resp.activities.filter(x => x.title.toLowerCase().includes(searchTerm) ||
					x.content.toLowerCase().includes(searchTerm));
				setSearchResultLength(filteredActivities.length);
				setData(prevData => {
					return { ...prevData, ...{ loading: false, activities: filteredActivities } };
				});
			}

		}
	}, [filter, showSnackbar]);

	// load activity data
	useEffect(() => {
		loadActivities();
		return () => {
			activitiesFido.dropIt();
		};
	}, [loadActivities]);

	// load topic filter data
	const loadTopics = async () => {
		let resp = await topicFido.fetch("/activities/library-topics")
			.catch(error => showSnackbar(error.message));

		if (resp && isMountedRef.current) {
			console.log("loadTopics - setTopics: ", resp);
			resp.forEach(topic => {
				// if freemium account, only enable Self Harm topic
				if (!authenticatedUser.isFreemium || topic.name == "Self Harm") {
					topic.selected = true;
					topic.disabled = false;
				}
				else {
					topic.selcted = false;
					topic.disabled = true;
				}
			});

			setTopics(resp);
			topicCache.current = resp;
		}
	};

	// user clicked status tab
	const tabClicked = (event, item) => {
		const storedCategory = JSON.parse(localStorage.getItem('category')) || [];

		let defaultCategories = storedCategory ? storedCategory : getDefaultCategories(item),
			defaultSources = getDefaultSources(),
			defaultAccounts = defaultFilterAccounts,
			defaultTopics = getDefaultTopics(),
			isSaved = item === Constants.ACTIVITY.CATEGORIES.SAVED.FILTER.ID;

		//setScrollTo(0);
		history.push("/inbox");

		// when moving status on mobile, it gets filtered out when going back,
		// but if you change buckets, it gets filter out of the newly loaded data
		if (!atLeastSM) {
			setAnimateOutId(null);
		}

		setSelected(prev => {
			return { ...prev, ...{ tab: item, index: -1, activityId: null, activity: null } };
		});

		setFilter(prev => {
			console.log(prev, item);
			return {
				...prev,
				...{ status: isSaved ? null : item, view: isSaved ? 'saved' : null },
				...{ category: defaultCategories, source: defaultSources, topic: defaultTopics }
			};
		});

		setCategories(defaultCategories);
		setSources(defaultSources);
		setAccounts(defaultAccounts);
		setTopics(defaultTopics);
	};

	const toggleRemoved = (event) => {
		let checked = event.target.checked;

		history.push("/inbox");

		setSelected(prev => {
			return { ...prev, ...{ index: -1, activityId: null, activity: null } };
		});

		setFilter(prev => {
			console.log("toggleRemoved - checked, newFilter: ", checked, { ...prev, ...{ view: checked ? "removed" : null } });
			return { ...prev, ...{ view: checked ? "removed" : null } };
		});
	};

	const clearTextHandler = () => {
		setSearchTerm('');
		setData(originalData);
	}

	const searchTextHandler = (value) => {
		let filteredActivities = [];
		const searchTerm = value.toLowerCase().trim();

		if (!value) {
			setSearchTerm('');
			setData(originalData);
		} else if (searchTerm.length > 2) {
			setSearchTerm(searchTerm);
			filteredActivities = originalData.activities.filter(x => x.title.toLowerCase().includes(searchTerm) ||
				x.content.toLowerCase().includes(searchTerm));
			setSearchResultLength(filteredActivities.length);
			setData(prevData => {
				return { ...prevData, ...{ loading: false, activities: filteredActivities } };
			});
		} else {
			setData(originalData);
		}
	}

	const filterHandler = (option, name, selectAll, reset, array, defaultValue, setter, filteredSelectAll) => {
		let result = filterHandlerHook(option, name, selectAll, reset, array, defaultValue, setter, filteredSelectAll);

		if (result.newArray.some(opt => opt.selected)) {
			history.push("/inbox");
			setSelected(prev => {
				return { ...prev, ...{ index: 0, activityId: null, activity: null } };
			});

			// it's false when UNselecting all so we don't filter in that case
			if (selectAll !== false) {
				let newFilter,
					newObj = {},
					selections = !reset ? result.newArray : result.defaults;

				if (name === 'category') {
					localStorage.setItem('category', JSON.stringify(selections));
				}

				newObj[name] = selections;
				newFilter = { ...filter, ...newObj };
				setFilter(prev => {
					console.log("filterActivities - prev, filter, selectedItems, newFilter: ", prev, filter, selections, { ...prev, ...newFilter });
					const updatedFilter = { ...prev, ...newFilter };
					return updatedFilter;
				});
			}
		}
	};

	// paging
	//const loadMore = async (page, overwrite, numberOfPages, autoloadFired = false) => {
	const loadMore = (page, overwrite, numberOfPages, autoloadFired = false) => {
		console.log("loadMore - data: ", data);
		if (data.hasMoreItems && !data.loading) {
			if (autoloadFired === false) {
				setData(prevData => {
					return { ...prevData, ...{ loading: true } };
				});
				setOriginalData(prevData => {
					return { ...prevData, ...{ loading: true } };
				});
			}

			let filterQuery = { ...filter };
			Object.keys(filter).forEach(key => {
				if (filter[key] != null && Array.isArray(filter[key])) {
					let array = [];
					array = filter[key].filter(item => {
						return item.selected;
					});
					filterQuery[key] = array.map(item => {
						return item.id;
					});
				}
			});
			console.log("loadMore - filterQuery: ", filterQuery);

			let //resp,
				query = {
					page: page,
					...filterQuery
				};

			if (numberOfPages) {
				delete query.page;
				query.numberOfPages = numberOfPages;
			}

			pagingFido.dropIt();
			//resp = await pagingFido.fetch("/activities", {query: query})
			return pagingFido.fetch("/activities", { query: query })
				.then(resp => {
					if (resp && isMountedRef.current) {
						setData(prevData => {
							// filter out any items that may have pending actions (move status, delete etc...)
							let acts = resp.activities.filter(item => !actionPending.find(item2 => item.id === item2.id)),
								dataActs = data.activities.filter(item => !actionPending.find(item2 => item.id === item2.id));

							console.log("loadMore response: actionPending, acts, dataActs: ", actionPending, acts, dataActs);
							console.log("loadMore response: overwrite, acts, (merged acts): ", overwrite, acts, [...dataActs, ...acts]);
							return {
								...prevData,
								...{
									loading: false,
									lastPage: resp.lastPage || 1,
									hasMoreItems: resp.hasMoreItems,
									activities: overwrite ? acts : [...dataActs, ...acts]
								}
							};
						});
						setOriginalData(prevData => {
							// filter out any items that may have pending actions (move status, delete etc...)
							let acts = resp.activities.filter(item => !actionPending.find(item2 => item.id === item2.id)),
								dataActs = data.activities.filter(item => !actionPending.find(item2 => item.id === item2.id));

							console.log("loadMore response: actionPending, acts, dataActs: ", actionPending, acts, dataActs);
							console.log("loadMore response: overwrite, acts, (merged acts): ", overwrite, acts, [...dataActs, ...acts]);
							return {
								...prevData,
								...{
									loading: false,
									lastPage: resp.lastPage || 1,
									hasMoreItems: resp.hasMoreItems,
									activities: overwrite ? acts : [...dataActs, ...acts]
								}
							};
						});
					}

					return Promise.resolve(true);
				})
				.catch(error => showSnackbar(error.message));

			/*if (resp && isMountedRef.current) {
				setData(prevData => {
					// filter out any items that may have pending actions (move status, delete etc...)
					let acts = resp.activities.filter(item => !actionPending.includes(item.id)),
						dataActs = data.activities.filter(item => !actionPending.includes(item.id));
					console.log("%%%%%%%%%%%%%%%% loadMore response: actionPending, acts, dataActs: ", actionPending, acts, dataActs );
					console.log("%%%%%%%%%%%%%%%%: overwrite, (merged acts): ", overwrite, [...dataActs, ...acts]);
					return {
						...prevData,
						...{
							loading: false,
							lastPage: resp.lastPage || 1,
							hasMoreItems: resp.hasMoreItems,
							activities: overwrite ? acts : [...dataActs, ...acts]
						}
					};
				});
			}*/
		} else {
			return Promise.resolve(false);
		}
	};

	let prevSelected = usePrevious(selected);
	const loadActivity = useCallback((force) => {
		if ((selected.activityId && prevSelected && prevSelected.activityId !== selected.activityId) || force) {
			console.log("load selected activity hook - prevSelected.activityId, selected.activityId: ", prevSelected.activityId, selected.activityId);

			// if redirected, already have data
			if (location.state && location.state.activity) {
				setSelected(prev => {
					return { ...prev, ...{ activity: location.state.activity } };
				});
			} else {
				// TEMP
				// ***********************
				console.log("setting selected.activity to null");
				setSelected(prev => {
					return { ...prev, ...{ activity: null } };
				});
				//setActivity(singleActivity);
				// ***********************

				if (isDetails || selected.activityId) {
					selectedActivityFido.dropIt();
					(async () => {
						let resp = await selectedActivityFido.fetch(`/activities/${selected.activityId}`)
							.catch((error) => {
								setActivityFidoError(true);
								if (error.message === "404 Not Found") {
									showSnackbar({
										message: "Not found. Outdated?",
										duration: false
									});
								} else {
									showSnackbar(error.message);
								}
							});

						if (resp && isMountedRef.current) {
							setSelected(prev => {
								return { ...prev, ...{ activity: resp.activity, tab: resp.activity.status ? resp.activity.status.status : defaultTab } };
							});
						}
					})();
				}
			}
		}
	}, [selected.activityId, prevSelected, location.state, isDetails, showSnackbar, setActivityFidoError]);

	// load selected activity
	useEffect(() => {
		loadActivity();
	}, [loadActivity]);

	// on mobile, if going straight to an activity, load it
	useEffect(() => {
		if (!atLeastSM && isDetails) {
			console.log("isDetails && mobile - load activity");
			let urlId = Number(isDetails[0].split("/")[2]);
			setSelected(prev => {
				return { ...prev, ...{ activityId: urlId } };
			});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const cardClicked = (cardIndex, activityId) => {
		if (activityId !== selected.activityId || !atLeastSM) {
			console.log("cardClicked - cardIndex, activityId: ", cardIndex, activityId);
			setSelected({ ...selected, ...{ index: cardIndex, activityId: activityId, activity: null } });

			// when clicking on the same card twice, since activityId doesn't change,
			// the hook below that changes history doesn't run for mobile
			if (!atLeastSM && activityId === selected.activityId) {
				console.log("cardClicked - history.push for mobile");
				history.push(`/inbox/${activityId}`);
				loadActivity(true);
			}
		}
	};

	// select an activity / card
	let prevData = usePrevious(data);
	useEffect(() => {
		let urlId = isDetails ? Number(isDetails[0].split("/")[2]) : null;
		if (atLeastSM && data.activities && data.activities.length
			&& !isJsonEqual(prevData.activities, data.activities)) {
			let index,
				activityId,
				newSelected;

			if (urlId !== prevSelected.activityId) {
				index = data.activities.findIndex(item => item.id === urlId);
			} else {
				index = prevSelected.index === -1 ? 0 : prevSelected.index;
				if (prevSelected.index === data.activities.length) {
					index = prevSelected.index - 1;
				}
			}

			// sometimes activity can be null?
			let activity = data.activities[index];
			activityId = index >= 0 && activity ? activity.id : urlId;
			newSelected = { ...prevSelected, ...{ index: index, activityId: activityId } };

			if (!isJsonEqual(prevSelected, newSelected)) {
				console.log("prevSelected !== newSelected (activities): ", prevData.activities, data.activities);
				console.log("select a card - index, newSelected, activityId: ", index, newSelected, activityId);
				setSelected(newSelected);
			}
		}
	}, [data.activities, prevSelected, prevData, isDetails, atLeastSM]);

	// when selected.activityId updated, navigate to it
	useEffect(() => {
		if (selected.activityId) {
			console.log("selected.activityId changed - activityId: ", selected.activityId);
			history.push(`/inbox/${selected.activityId}`);
		}
	}, [selected.activityId, history]);

	// scroll to activity
	useEffect(() => {
		if (data.activities && data.activities[selected.index]
			&& selected.index >= 0 && prevSelected.activityId !== selected.activityId) {
			let card = document.querySelector(`[id='${selected.activityId}']`);
			console.log(`scroll to activity effect - selected.index, selected.activityId, ${card ? "card.offsetTop" : "card is null"}:`, selected.index, selected.activityId, card ? card.offsetTop : null);
			if (card) {
				setScrollTo(card.offsetTop);
			}
		}
	}, [selected.index, selected.activityId, prevSelected, data.activities]);

	const filterOutActivity = (itemId) => {
		let newItems = data.activities.filter(item => {
			// eslint-disable-next-line eqeqeq
			return item.id != itemId;
		});

		console.log("filterOutActivity - newItems: ", newItems);

		if (selected.activityId === itemId) {
			setSelected({ ...selected, ...{ index: -1, activityId: null } });
		}

		setData({ ...data, ...{ activities: newItems } });
		setOriginalData({ ...data, ...{ activities: newItems } });
	};

	const animateExited = (itemId) => {
		setAnimateOutId(null);
		filterOutActivity(itemId);
	};

	/*const smarterLoadMore = async () => {
		if (data.lastPage === 1) {
			await loadMore(data.lastPage, true);
		} else {
			await loadMore(null, true, data.lastPage);
		}
	};*/

	const smarterLoadMore = () => {
		if (data.lastPage === 1) {
			return loadMore(data.lastPage, true);
		} else {
			return loadMore(null, true, data.lastPage);
		}
	};

	const postActionHandler = (activityId, actionFailedError) => {
		console.log("postActionHandler...");
		if (!actionFailedError) {
			completeActionPending(activityId);
		} else {
			purgeActionPending(activityId);
		}

		return smarterLoadMore().then(result => {
			console.log("postActionHandler: activityId, result: ", activityId, result);
			if (result === true) {
				purgeActionPending();
			}
		});
	};

	const animateOutHandler = (activityId) => {
		setAnimateOutId(activityId);
		addActionPending(activityId);
	};

	const updateActivityHandler = (data) => {
		if (data && data.activity) {
			console.log("updating activity - activity: ", data.activity);
			setSelected({ ...selected, ...{ activity: data.activity } });
		}
	};

	const markRead = (activityId) => {
		let index,
			activity,
			clone;

		if (!authenticatedUser.ghosting && data.activities && data.activities.length) {
			activity = data.activities.find((item, idx) => {
				if (item.id === activityId) {
					index = idx;
					return true;
				}
				return false;
			});

			clone = { ...activity };

			if (clone &&
				(!clone.views || !clone.views.length ||
					!Constants.ACTIVITY.hasViewed(authenticatedUser, clone.views))) {
				clone.views = clone.views || [];
				clone.views.push({
					user_name: authenticatedUser.username,
					userId: authenticatedUser.id
				});

				console.log("markRead - index, clone: ", index, clone);
				let activities = [...prevData.activities];
				activities[index] = clone;
				if (!isJsonEqual(activities, prevData.activities)) {
					setData({ ...prevData, ...{ activities: activities } });
					setOriginalData({ ...prevData, ...{ activities: activities } });
				}
			}
		}
	};

	const addActionPending = (activityId) => {
		if (!actionPending.find(item => item.id === activityId)) {
			actionPending = [...actionPending, { id: activityId, complete: false }];
			console.log("addActionPending: activityId, actionPending - ", activityId, actionPending);
		}
	};

	const completeActionPending = (activityId) => {
		let index = actionPending.findIndex(item => item.id === activityId);
		console.log("removeActionPending: activityId, actionPending, index - ", activityId, actionPending, index);
		if (index >= 0) {
			actionPending[index].complete = true;
			console.log("removeActionPending: actionPending - ", actionPending);
		}
	};

	const purgeActionPending = (activityId) => {
		if (activityId) {
			let index = actionPending.findIndex(item => item.id === activityId);
			console.log("purgeActionPending: activityId, actionPending, index - ", activityId, actionPending, index);
			if (index >= 0) {
				actionPending.splice(index, 1);
				console.log("purgeActionPending: actionPending - ", actionPending);
			}
		} else {
			actionPending = actionPending.filter(item => item.complete === false);
			console.log("purgeActionPending: removed all items that are not complete, actionPending - ", actionPending);
		}
	};

	const careStatus = (activityId, newStatus, msg) => {

		let poodle = new Fido();
		poodle.fetch(`/activities/${activityId}/care-status/${newStatus}`, {
			method: "PUT",
		}, msg, "Care Status Updated.")
			.then(async (data) => {
				updateActivityHandler(data)
			})
			.catch(error => {
				showSnackbar(error.message);
			});
	}

	const moveToStatus = (activityId, newStatus, msg) => {
		let acts = data.activities,
			itemIndex = acts ? acts.findIndex(item => item.id === activityId) : -1,
			// selected bucket is not saved and the activity is actually selected in the list
			shouldAnimateOut = savedConstants.FILTER.ID !== selected.tab && (activityId === selected.activityId || itemIndex > -1);

		showSnackbar(false);

		if (shouldAnimateOut) {
			animateOutHandler(activityId);
		}

		let poodle = new Fido();
		poodle.fetch(`/activities/${activityId}/status/${newStatus}`, {
			method: "PUT",
		}, msg, "Status Updated.")
			.then(async (data) => {
				if (savedConstants.FILTER.ID === selected.tab) {
					history.push(location.pathname, { activity: data.activity });
				}

				// TODO: maybe actionPending is going to get rid of need for changingStatus / binocular hack?
				changingStatus = false;

				if (!atLeastSM) {
					if (isDetails) {
						// otherwise potential of not actually backing up if activity url's in history
						history.push("/inbox");
					}
				} else if (activityId !== selected.activityId && itemIndex === -1 && data.activities && data.activities.length) {
					cardClicked(0, data.activities[0].id);
				} else if (activityId === selected.activityId && itemIndex === -1) {
					console.log("moveToStatus - card not in current list, activityId, selected.activityId, prevSelected.activityId, newId, itemIndex, data.activities, acts", activityId, selected.activityId, prevSelected.activityId, itemIndex, data.activities, acts);
					setSelected(prev => {
						return { ...prev, ...{ index: 0, activity: null, activityId: acts[0].id } };
					});
				}

				// update the esclated counter 
				const numberCircleElement = document.querySelector('#eslcatedCount');
				if (numberCircleElement) {
					let fidoo = new Fido();
					fidoo.fetch('/esclatedAlerts/esclatedCount', {
						method: "GET"
					})
						.then((data) => {
							numberCircleElement.innerHTML = data.count;
						});
				}

				if (shouldAnimateOut) {
					return postActionHandler(activityId);
				}
			})
			.catch(error => {
				showSnackbar(error.message);
				return postActionHandler(activityId, error);
			});
	};

	const forReviewClicked = (e, activityId) => {
		e.preventDefault();
		e.stopPropagation();

		// if clicking binoculars too quickly, cards can get into a weird state SNT-272
		if (changingStatus === false) {
			changingStatus = true;
			moveToStatus(activityId, Constants.ACTIVITY.STATUSES.FOR_REVIEW.ID, "Needs Review...");
		}
	};

	const upgradeCloseHandler = (upgrade) => {
		setUpgradeFreemiumOpen(false);

		if (upgrade) {
			let fido = new Fido();
			fido.fetch(`/accounts/${authenticatedUser.accountId}/upgrade-freemium`, {
				method: "PUT"
			}, 'Upgrading Account...', 'Account upgraded.')
				.then(data => {
					console.log('data', data);
					window.location.reload();
				})
				.catch(error => showSnackbar(error.message));
		}
	}

	const upgradeButtonClick = () => {
		console.log('clicked!!');
		setUpgradeFreemiumOpen(true);
	}

	let activityList = (
		<ActivityList
			history={history}
			items={data.activities}
			isLoading={data.loading}
			lastPage={data.lastPage}
			hasMore={data.hasMoreItems}
			loadMore={loadMore}
			selectedIndex={selected.index}
			animateOutId={animateOutId}
			onAnimateExited={animateExited}
			clickHandler={cardClicked}
			currentStatus={selected.tab}
			scrollTo={scrollTo}
			//getScrollAreaRef={getScrollAreaRef}
			scrollGridClassName={classes.scrollGrid}
			pulsateAlerts={filter.category.every(item => item.selected)}
			searchTerm={searchTerm}
			actionButtons={[{
				title: "Needs Review",
				icon: ["fas", "binoculars"],
				clickHandler: forReviewClicked,
				show: (itemProps) => {
					return itemProps.currentStatus !== Constants.ACTIVITY.STATUSES.FOR_REVIEW.ID &&
						itemProps.currentStatus !== Constants.ACTIVITY.STATUSES.ESCALATED.ID && !itemProps.isDeleted;
				}
			}]} />
	),

		activityComponent = data.loading || (data.loading === false && data.activities && data.activities.length !== 0) ? (
			<Activity
				{...props}
				activity={selected.activity}
				viewedCallback={markRead}
				showRemoved={filter.view === "removed"}
				currentStatus={selected.tab}
				moveStatusHandler={moveToStatus}
				careStatusHandler={careStatus}
				postActionHandler={postActionHandler}
				onAnimateOutHandler={animateOutHandler}
				setActivityFidoError={activityFidoError}
				updateActivityHandler={updateActivityHandler}
				searchTerm={searchTerm} />
		) : null;

	return (
		<Frag>
			{(!atLeastSM && !isDetails) || atLeastSM ?
				<InboxTabs
					tabClicked={tabClicked}
					selectedTab={selected.tab} /> : null
			}
			<PaddedContent className={isDetails ? classes.paddedContent : null}>
				{(!atLeastSM && !isDetails) || atLeastSM ?
					<InboxFilters
						topics={topics}
						location={location}
						showRemovedHandler={toggleRemoved}
						showRemovedChecked={filter.view === "removed"}
						showRemovedCheckbox={selected.tab === Constants.ACTIVITY.STATUSES.RESOLVED.ID}
						categoryOptions={categories}
						sourceOptions={sources}
						accountOptions={accounts}
						topicOptions={topics}
						isFreemium={authenticatedUser.isFreemium}
						clearTextHandler={clearTextHandler}
						searchTextHandler={searchTextHandler}
						categoryFilterHandler={(option, name, selectAll, reset, filteredSelectAll) => {
							filterHandler(option, name, selectAll, reset, categories, getDefaultCategories(), setCategories, filteredSelectAll);
						}}
						sourceFilterHandler={(option, name, selectAll, reset, filteredSelectAll) => {
							filterHandler(option, name, selectAll, reset, sources, null, setSources, filteredSelectAll);
						}}
						accountFilterHandler={(option, name, selectAll, reset, filteredSelectAll) => {
							filterHandler(option, name, selectAll, reset, accounts, null, setAccounts, filteredSelectAll);
						}}
						topicFilterHandler={(option, name, selectAll, reset, filteredSelectAll) => {
							if (!option.disabled) {
								filterHandler(option, name, selectAll, reset, topics, null, setTopics, filteredSelectAll);
							}
						}}
						stickyFilterHandler={(localStorageKey, isSticky) => stickyFilterHandler(authenticatedUser, accounts, localStorageKey, isSticky)}
						freemiumUpgradeHandler={upgradeButtonClick} /> : null
				}
				{searchTerm.length > 2 && <Typography className={classes.searchResult}>Showing search result(s) for <span className={classes.bold}>{searchTerm}</span></Typography>}
				<Grid container>
					<Grid
						item
						className={classes.mainGridItem}
						xs={12} sm={4} md={3} lg={3} xl={3}>
						{!atLeastSM ?
							isDetails ? activityComponent : activityList
							: activityList
						}
					</Grid>
					{atLeastSM ?
						<Grid item xs={12} sm={8} md={9} lg={9} xl={9}>
							{activityComponent}
						</Grid> : null
					}
				</Grid>
			</PaddedContent>
			<UpgradeFreemiumDialog
				open={upgradeFreemiumOpen}
				closeHandler={upgradeCloseHandler} />
		</Frag>
	);
}, (prevProps, newProps) => {
	// if they're the same, don't re-render (return true)
	return isJsonEqual(prevProps, newProps);
});

Activities.displayName = "Activities";

Activities.propTypes = {
	history: PropTypes.object.isRequired,
	location: PropTypes.object.isRequired,
	updateHeader: PropTypes.func.isRequired,
	resetHeader: PropTypes.func.isRequired,
	showSnackbar: PropTypes.func.isRequired,
	setBannerClassName: PropTypes.func.isRequired,
	hasNudgeBanner: PropTypes.bool,
	updateUser: PropTypes.func
};

Activities.whyDidYouRender = Constants.ENV.IS_LOCAL;

export default Activities;